import { EMPTY, map, Observable, of, race, switchMap, withLatestFrom } from 'rxjs';
import { ofType } from 'src/core/engine/rxjs/ofType';
import { DataStatus, EpicDeps } from 'src/core/engine';
import { AppendUserState, UserActions } from 'src/core/state/user';
import { LOCATION_CHANGE } from 'src/core/engine';
import { AppendRouterState, RouterActions } from 'src/core/engine/router/router.register';

import { Actions, CartMsg, Msg, STORAGE_KEY_CART, TypeMap } from '../cart.actions';

type Action = TypeMap['Cart.Refresh'];
type AllActions = Actions | UserActions | RouterActions;

/**
 * This epic will determine if the current stored cart should be updated/created
 * @param action$
 * @param state$
 * @param deps
 */
export function cartRefresh(
    action$: Observable<AllActions>,
    state$: Observable<AppendRouterState<AppendUserState>>,
    deps: EpicDeps,
): Observable<any> {
    const pathname$ = state$.pipe(map((s) => s?.router?.location?.pathname));
    return action$.pipe(
        ofType<AllActions, Action>('Cart.Refresh'),
        withLatestFrom(pathname$, state$.pipe(map((s) => s?.user))),
        switchMap(([action, pathname, user]) => {
            /**
             * If the user is currently refreshing
             *
             *  - success: reply this action
             *  - failure: do nothing
             */
            if (user.status === DataStatus.Pending) {
                // replay the action if the user data get's persisted
                const afterUser$ = action$.pipe(
                    ofType<AllActions>('User.Persist'),
                    map(() => action),
                );
                // do nothing if the user becomes signed out
                const userError$ = action$.pipe(
                    ofType<AllActions>('User.SignOut', LOCATION_CHANGE),
                    map(() => CartMsg('Cart.Noop', `doing nothing,  'user refresh' errored or navigation occurred`)),
                );
                return race(afterUser$, userError$);
            }
            /**
             * If we're in any checkout page, just don't perform
             * any cart refreshing - instead we allow checkout implementations
             * to trigger the refresh with Msg('Cart.Fetch');
             */
            if (pathname && pathname.indexOf('/checkout') === 0 && pathname.indexOf('/checkout/success') === -1) {
                return EMPTY;
            }

            const existing = (deps.storage.get(STORAGE_KEY_CART) || {}).cartId;
            const wasForced = action.payload.force;

            if (existing && wasForced) {
                return of(Msg('Cart.Fetch', undefined));
            }

            if (existing && !wasForced) {
                return of(Msg('Cart.FetchSkipped'));
            }

            return of(Msg('Cart.Fetch', undefined));
        }),
    );
}
